import React, { useState, useEffect } from "react";
import { BarChart3, TrendingUp, PoundSterling, Target, ListChecks, Plus, CheckCircle2, Circle, AlarmClock } from "lucide-react";
import { getAnalytics, getTasks, createTask, toggleTask } from "../../api";
import { useToast } from "../../hooks/use-toast";

const fmt = (n) => `\u00A3${Number(n || 0).toLocaleString()}`;
const LEAD_STAGES = ["new", "contacted", "qualified", "viewing", "offer", "won", "lost"];

const Stat = ({ icon: Icon, label, value, accent }) => (
  <div className="bg-white rounded-2xl border border-[#f0ece5] p-5">
    <Icon size={18} className={accent || "text-[#b8935f]"} />
    <p className="font-display text-[26px] font-500 text-[#2b2b2b] mt-2">{value}</p>
    <p className="text-[12px] text-[#8a8a8a] uppercase tracking-wide">{label}</p>
  </div>
);

const AdminAnalytics = () => {
  const { toast } = useToast();
  const [a, setA] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [newTask, setNewTask] = useState("");
  const refresh = () => { getAnalytics().then(setA).catch(() => {}); getTasks().then(setTasks).catch(() => {}); };
  useEffect(() => { refresh(); }, []);

  const addTask = async (e) => {
    e.preventDefault();
    if (!newTask.trim()) return;
    try { await createTask({ title: newTask.trim(), priority: "normal" }); setNewTask(""); getTasks().then(setTasks); toast({ title: "Task added" }); }
    catch { toast({ title: "Couldn't add task" }); }
  };
  const flip = async (id) => { try { await toggleTask(id); getTasks().then(setTasks); } catch { toast({ title: "Update failed" }); } };

  if (!a) return <div className="bg-white rounded-2xl border border-[#f0ece5] p-8 text-center text-[#8a8a8a]">Loading analytics…</div>;
  const maxLead = Math.max(1, ...LEAD_STAGES.map((s) => a.leadFunnel[s] || 0));
  const overdue = tasks.filter((t) => t.overdue && !t.done).length;

  return (
    <div className="space-y-6" data-testid="admin-analytics">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat icon={PoundSterling} label="Forecast fees" value={fmt(a.forecastRevenue)} />
        <Stat icon={Target} label="Lead conversion" value={`${a.conversionRate}%`} />
        <Stat icon={TrendingUp} label="Active deals" value={a.activeDeals} />
        <Stat icon={BarChart3} label="Total leads" value={a.totalLeads} />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl border border-[#f0ece5] p-4 sm:p-6">
          <h3 className="font-display text-[18px] font-500 text-[#2b2b2b] mb-4">Lead Funnel</h3>
          <div className="space-y-2">
            {LEAD_STAGES.map((s) => (
              <div key={s} className="flex items-center gap-3" data-testid="funnel-row">
                <span className="w-20 text-[12px] text-[#6a6a6a] capitalize">{s}</span>
                <div className="flex-1 h-6 rounded-md bg-[#f5f1ea] overflow-hidden"><div className="h-full bg-[#b8935f]/80 rounded-md transition-all duration-500" style={{ width: `${(a.leadFunnel[s] || 0) / maxLead * 100}%` }} /></div>
                <span className="w-8 text-right text-[13px] font-500 text-[#2b2b2b]">{a.leadFunnel[s] || 0}</span>
              </div>
            ))}
          </div>
          <h3 className="font-display text-[18px] font-500 text-[#2b2b2b] mt-6 mb-3">Source Performance</h3>
          <div className="space-y-2">
            {a.sourcePerformance.map((s) => (
              <div key={s.source} className="flex items-center justify-between text-[13px]">
                <span className="text-[#4a4a4a] capitalize">{s.source}</span>
                <span className="text-[#8a8a8a]">{s.total} leads · <span className="text-green-600 font-500">{s.rate}% won</span></span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-[#f0ece5] p-4 sm:p-6" data-testid="tasks-panel">
          <h3 className="font-display text-[18px] font-500 text-[#2b2b2b] mb-4 flex items-center gap-2"><ListChecks size={18} className="text-[#b8935f]" /> Tasks {overdue > 0 && <span className="text-[12px] px-2 py-0.5 rounded-full bg-red-100 text-red-600 flex items-center gap-1"><AlarmClock size={11} /> {overdue} overdue</span>}</h3>
          <form onSubmit={addTask} className="flex gap-2 mb-4">
            <input value={newTask} onChange={(e) => setNewTask(e.target.value)} placeholder="Add a task…" data-testid="new-task-input" className="flex-1 h-10 px-3 rounded-lg border border-[#e2d8c8] text-[13px] outline-none focus:ring-2 focus:ring-[#b8935f]/30 focus:border-[#b8935f]" />
            <button type="submit" data-testid="add-task-btn" className="px-4 rounded-lg bg-[#b8935f] text-white text-[13px] font-500 hover:bg-[#a67f4d] transition-colors"><Plus size={16} /></button>
          </form>
          <div className="space-y-2 max-h-[380px] overflow-y-auto">
            {tasks.map((t) => (
              <button key={t.id} onClick={() => flip(t.id)} data-testid="task-row" className={`w-full flex items-center gap-2.5 p-3 rounded-lg border text-left transition-colors ${t.done ? "bg-[#faf7f2] border-[#f0ece5] opacity-60" : t.overdue ? "bg-red-50/50 border-red-200" : "bg-white border-[#f0ece5] hover:border-[#b8935f]"}`}>
                {t.done ? <CheckCircle2 size={16} className="text-green-600 flex-shrink-0" /> : <Circle size={16} className="text-[#b8935f] flex-shrink-0" />}
                <span className={`text-[13px] flex-1 ${t.done ? "line-through text-[#a0a0a0]" : "text-[#2b2b2b]"}`}>{t.title}</span>
                {t.due && <span className={`text-[11px] ${t.overdue && !t.done ? "text-red-600" : "text-[#a0a0a0]"}`}>{new Date(t.due).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}</span>}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminAnalytics;
